/** DRW × FLD-OFF — markups made with no signal. The write sits in the shared outbox; this module keeps
 *  the picture honest meanwhile: a pending markup is drawn on the sheet (dashed, "waiting to sync"),
 *  an offline install tap / resolve / reply / delete is overlaid on the cached row, and every local
 *  trace is dropped the moment the server accepts the replay. Keys: pending_markups:{sheetId},
 *  pending_patches (markupId → fields). */
import { idb } from "../fieldops/offline/db";

const KEY = (sheetId) => `pending_markups:${sheetId}`;
const PATCHES = "pending_patches";
const me = () => { try { return JSON.parse(localStorage.getItem("auth_user") || "{}"); } catch { return {}; } };
const formVal = (body, k) => (body?.form || []).find((p) => p.k === k)?.v;

async function remember(sheetId, m) {
  const cur = (await idb.get("meta", KEY(sheetId))) || [];
  await idb.set("meta", KEY(sheetId), [...cur.filter((x) => x.id !== m.id), m]);
  const idx = (await idb.get("meta", "pending_markup_sheets")) || {};
  idx[m.id] = sheetId; await idb.set("meta", "pending_markup_sheets", idx);
}
async function patch(markupId, recId, fields) {
  const cur = (await idb.get("meta", PATCHES)) || {};
  cur[markupId] = { ...(cur[markupId] || {}), ...fields, _recs: [...((cur[markupId] || {})._recs || []), recId] };
  await idb.set("meta", PATCHES, cur);
}

/** A write left the device unsent → the local picture of it. */
async function onQueued(rec, body, cfg) {
  const u = String(rec.url);
  const who = me();
  const base = { id: rec.id, client_id: rec.id, pending: true, origin: "app", layer: cfg._layer || "Field",
    author: who.name || who.email || rec.user || "me", author_email: who.email || "", status: "open", replies: [],
    room_ref: {}, space_name: "", verify_state: "", is_deleted: false, created_at: rec.client_timestamp, updated_at: rec.client_timestamp };
  let mm;
  if ((mm = u.match(/^\/sheets\/([^/]+)\/markups$/))) {
    const j = body?.json || {};
    await remember(mm[1], { ...base, sheet_id: mm[1], type: j.type, geometry: j.geometry, style: j.style || {},
      subject: j.subject || j.stamp || "", stamp: j.stamp || "", label: j.label || "", comments: j.comments || "",
      product_code: j.type === "symbol" ? j.subject : "" });
  } else if ((mm = u.match(/^\/sheets\/([^/]+)\/photo-pin$/))) {
    await remember(mm[1], { ...base, sheet_id: mm[1], type: "photo_pin", subject: "Photo", comments: formVal(body, "comments") || "",
      geometry: { x: Number(formVal(body, "x")), y: Number(formVal(body, "y")) }, local_thumb: `thumb:${rec.id}` });
  } else if ((mm = u.match(/^\/sheets\/([^/]+)\/punch-pin$/))) {
    const j = body?.json || {};
    await remember(mm[1], { ...base, sheet_id: mm[1], type: "punch_pin", subject: "Punch (pending)", label: "?",
      comments: j.description || "", geometry: { x: j.x, y: j.y } });
  } else if ((mm = u.match(/^\/markups\/([^/]+)\/replies$/))) {
    await patch(mm[1], rec.id, { reply: { id: rec.id, author: who.name || "me", at: rec.client_timestamp, text: body?.json?.text || "", pending: true } });
  } else if ((mm = u.match(/^\/markups\/([^/]+)\/carry$/))) {
    const j = body?.json || {};
    await patch(mm[1], rec.id, j.action === "dismiss" ? { is_deleted: true } : j.action === "move" ? { geometry: j.geometry, verify_state: "moved" } : { verify_state: "kept" });
  } else if ((mm = u.match(/^\/markups\/([^/]+)$/))) {
    await patch(mm[1], rec.id, rec.method === "DELETE" ? { is_deleted: true } : { ...(body?.json || {}) });
  }
}

/** Server took it (or the user dropped it) → forget the local trace. */
async function onSettled(rec) {
  const idx = (await idb.get("meta", "pending_markup_sheets")) || {};
  const sheetId = idx[rec.id];
  if (sheetId) {
    const cur = (await idb.get("meta", KEY(sheetId))) || [];
    await idb.set("meta", KEY(sheetId), cur.filter((x) => x.id !== rec.id));
    delete idx[rec.id]; await idb.set("meta", "pending_markup_sheets", idx);
  }
  const patches = (await idb.get("meta", PATCHES)) || {};
  let touched = false;
  for (const [mid, p] of Object.entries(patches)) {
    if ((p._recs || []).includes(rec.id)) { delete patches[mid]; touched = true; }   // one settled write clears that markup's overlay; the reload shows the truth
  }
  if (touched) await idb.set("meta", PATCHES, patches);
}

/** The replay was refused → keep it on the sheet, but red, so the installer sees what to review. */
async function onConflict(rec) {
  const idx = (await idb.get("meta", "pending_markup_sheets")) || {};
  const sheetId = idx[rec.id];
  if (!sheetId) return;
  const cur = (await idb.get("meta", KEY(sheetId))) || [];
  await idb.set("meta", KEY(sheetId), cur.map((x) => (x.id === rec.id ? { ...x, conflict: true, conflict_detail: typeof rec.response?.detail === "string" ? rec.response.detail : "" } : x)));
}

export async function pendingFor(sheetId) { return (await idb.get("meta", KEY(sheetId))) || []; }

/** Apply every local trace to a sheet-detail or markups-list payload. */
export async function overlaySheet(sheetId, detail) {
  if (!detail || !Array.isArray(detail.markups)) return detail;
  const pend = sheetId ? await pendingFor(sheetId) : [];
  const patches = (await idb.get("meta", PATCHES)) || {};
  if (!pend.length && !Object.keys(patches).length) return detail;
  const seen = new Set(detail.markups.map((m) => m.client_id).filter(Boolean));
  let rows = detail.markups.map((m) => {
    const p = patches[m.id];
    if (!p) return m;
    const { _recs, reply, ...rest } = p;
    const out = { ...m, ...rest, pending_edit: true };
    if (reply) out.replies = [...(m.replies || []), reply];
    return out;
  }).filter((m) => !m.is_deleted);
  rows = rows.concat(pend.filter((m) => !seen.has(m.id)));
  return { ...detail, markups: rows };
}

async function overlay(cfg, data) {
  const u = String(cfg.url);
  let mm;
  if ((mm = u.match(/^\/sheets\/([^/]+)$/))) return overlaySheet(mm[1], data);
  if (/^\/projects\/[^/]+\/markups$/.test(u) && data?.markups) {
    const idx = (await idb.get("meta", "pending_markup_sheets")) || {};
    const sheets = [...new Set(Object.values(idx))];
    let out = await overlaySheet("", data);
    for (const s of sheets) out = { ...out, markups: [...out.markups, ...(await pendingFor(s))] };
    return out;
  }
  return data;
}

export const drawingsOffline = { onQueued, onAccepted: onSettled, onRemoved: onSettled, onConflict, overlay };
